const LEAGUE_TAG_PREFIX = 'pref_league - ';

let _favTeamsCache = null;

const TEAMS_GQL = `query Teams($cursor: String) {
  metaobjects(type: "sparky--team", first: 250, after: $cursor) {
    pageInfo { hasNextPage endCursor }
    nodes {
      id
      fields {
        key value
        reference { ... on MediaImage { image { url(transform: { maxWidth: 48 }) altText } } }
        references(first: 20) { nodes { ... on Metaobject { fields { key value } } } }
      }
    }
  }
}`;

const LEAGUES_GQL = `query Leagues($cursor: String) {
  metaobjects(type: "sparky--league", first: 50, after: $cursor) {
    pageInfo { hasNextPage endCursor }
    nodes {
      id
      fields {
        key value
        reference { ... on MediaImage { image { url(transform: { maxWidth: 48 }) altText } } }
      }
    }
  }
}`;

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseTeamEntry(entry) {
  const [handle, league = ''] = entry.split('|');
  return { handle: handle.toLowerCase(), league };
}

function handlesFromEntries(entries) {
  return new Set(entries.map(entry => parseTeamEntry(entry).handle));
}

function formatName(raw, override, { uppercase = false } = {}) {
  const normalized = (raw || '').replace(/-/g, ' ');
  const base = uppercase
    ? normalized.toUpperCase()
    : normalized.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  if (!override) return base;
  return base.replace(new RegExp(escapeRegex(override), 'i'), override);
}

async function paginate(shop, token, query, maxPages = 20) {
  const nodes = [];
  let cursor = null, page = 0;
  do {
    const response = await fetch(`https://${shop}/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query, variables: { cursor } }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const { data, errors } = await response.json();
    if (errors?.length) throw new Error(errors[0].message);
    const connection = data?.metaobjects;
    nodes.push(...(connection?.nodes ?? []));
    cursor = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
    page++;
  } while (cursor && page < maxPages);
  return nodes;
}

function parseTeam(node) {
  const team = { id: node.id, leagues: [] };
  for (const field of node.fields) {
    if (field.key === 'logo') {
      team.logo = field.reference?.image?.url ?? null;
    } else if (field.key === 'leagues') {
      team.leagues = (field.references?.nodes ?? [])
        .map(ref => ref.fields?.find(f => f.key === 'name')?.value || '')
        .filter(Boolean);
    } else {
      team[field.key] = field.value;
    }
  }
  return team;
}

function createFetcher(gql, transform) {
  let cache = null, fetching = null;
  return async (shop, token) => {
    if (cache) return cache;
    if (!fetching) {
      fetching = paginate(shop, token, gql)
        .then(nodes => { cache = transform(nodes); return cache; })
        .catch(err => { console.error('[customer-preferences] fetch:', err); fetching = null; return []; });
    }
    return fetching;
  };
}

const fetchTeams = createFetcher(TEAMS_GQL, nodes =>
  nodes.map(parseTeam)
    .filter(t => t.team_tag)
    .sort((a, b) => (a.name || a.team_tag).localeCompare(b.name || b.team_tag))
);

const fetchLeagues = createFetcher(LEAGUES_GQL, nodes =>
  nodes.map(node => {
    const league = { id: node.id };
    for (const field of node.fields) {
      league[field.key] = field.key === 'logo' ? (field.reference?.image?.url ?? null) : field.value;
    }
    return league;
  }).filter(l => l.name)
);

function saveToCF(selectedHandles, savedHandles, leagueMap, { selectedLeagues, allLeagues, gender, genderOptions } = {}) {
  CF.customer.set('favorite_teams_list', [...selectedHandles].map(handle =>
    `${handle.toUpperCase()}|${(leagueMap[handle] || '').toUpperCase()}`
  ));
  savedHandles.forEach(handle => { if (!selectedHandles.has(handle)) CF.customer.removeTag(`fav_team-${handle}`); });
  selectedHandles.forEach(handle => { if (!savedHandles.has(handle)) CF.customer.addTag(`fav_team-${handle}`); });

  if (selectedLeagues) {
    CF.customer.set('favorite_leagues', [...selectedLeagues].map(name => `${LEAGUE_TAG_PREFIX}${name}`));
    allLeagues?.forEach(league => {
      const leagueName = (league.name || '').toUpperCase();
      CF.customer[selectedLeagues.has(leagueName) ? 'addTag' : 'removeTag'](`${LEAGUE_TAG_PREFIX}${leagueName}`);
    });
  }

  if (genderOptions) {
    CF.customer.set('gender', gender);
    genderOptions.forEach(opt => {
      if (!opt.value) return;
      CF.customer[gender === opt.value ? 'addTag' : 'removeTag'](opt.value);
    });
  }
}

function fetchFromCF() {
  return {
    savedTeams:   CF.customer.get('favorite_teams_list') || [],
    savedLeagues: CF.customer.get('favorite_leagues') || [],
    savedGender:  CF.customer.get('gender') || '',
  };
}

function cfSave(formId) {
  const result = CF.customer.save({ formId });
  return result?.then ? result : new Promise(resolve => setTimeout(resolve, 1000));
}

function whenCFReady(callback) {
  return new Promise(resolve => {
    if (!window.CF?.customerReady) { resolve(); return; }
    window.CF.customerReady(async () => { await callback(); resolve(); });
  });
}

function showRowSpinner(button) {
  const wrap = button?.querySelector('[data-favorite-teams-icon-wrap]');
  wrap?.querySelectorAll('[data-favorite-teams-icon-empty], [data-favorite-teams-icon-filled]')
    .forEach(el => el.classList.add('hidden'));
  wrap?.classList.add('loading');
}

function hideRowSpinner(button, selected) {
  button?.querySelector('[data-favorite-teams-icon-wrap]')?.classList.remove('loading');
  button?.querySelector('[data-favorite-teams-icon-empty]')?.classList.toggle('hidden', selected);
  button?.querySelector('[data-favorite-teams-icon-filled]')?.classList.toggle('hidden', !selected);
}

function fillLogoSlot(slot, src, alt) {
  if (!slot) return;
  if (src) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt || '';
    img.className = `${slot.className} object-contain`.trim();
    img.loading = 'lazy';
    slot.replaceWith(img);
  } else if (!('chipLogoKeep' in slot.dataset)) {
    slot.remove();
  }
}

if (!customElements.get('customer-preferences')) {
  customElements.define('customer-preferences', class CustomerPreferences extends HTMLElement {
    static get observedAttributes() { return ['data-selected']; }

    attributeChangedCallback(name, oldVal, newVal) {
      if (oldVal !== null && oldVal !== newVal && this._initialized) this._syncFromSelected();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      window.addEventListener('modal:closed', () => this._reloadIfDirty());
      setTimeout(() => this._setup().catch(err => console.error('[customer-preferences]', err)), 0);
    }

    disconnectedCallback() {
      // Other account-dashboard widgets (hero team badge, team carousel) snapshot favorite
      // teams once at page load and don't observe live edits. Reload once on close, only if
      // this editing session actually changed something, so they stay in sync.
      this._reloadIfDirty();
    }

    // A toggle click can still have its CF save in flight when the modal closes
    // (e.g. closing right after clicking) — wait for it before reading _dirty,
    // since that flag is only set once the save resolves.
    async _reloadIfDirty() {
      await this._pendingSave;
      if (this._dirty && window.location.pathname === '/account') {
        window.location.reload();
      }
    }

    async _setup() {
      const shop  = this.dataset.shop;
      const token = this.dataset.token;
      this._isLoggedIn  = this.dataset.loggedIn !== 'false';
      this._isPrefsMode = !!this.querySelector('[data-action="save-prefs"]');

      const serverEntries = JSON.parse(this.dataset.selected || '[]');
      if (_favTeamsCache == null) _favTeamsCache = serverEntries;
      const savedEntries = _favTeamsCache;
      this._selectedTeamHandles = handlesFromEntries(savedEntries);
      this._savedTeamHandles    = new Set(this._selectedTeamHandles);
      this._leagueMap = Object.fromEntries(savedEntries.map(entry => {
        const { handle, league } = parseTeamEntry(entry);
        return [handle, league];
      }));

      if (this._isPrefsMode) {
        const savedLeagues = JSON.parse(this.dataset.leagues || '[]');
        this._selectedLeagues = new Set(
          savedLeagues?.map(tag => tag.replace(LEAGUE_TAG_PREFIX, '').trim().toUpperCase()) ?? []
        );
      } else {
        this._accountDrawerTrigger = document.querySelector('[data-modal-action="open"][data-modal-id^="account-drawer"]');
      }

      this._wireListeners();

      const [teams, leagues] = await Promise.all([
        fetchTeams(shop, token),
        this._isPrefsMode ? fetchLeagues(shop, token) : Promise.resolve(null),
      ]);

      this._allTeams = teams.filter(t => t.logo);
      teams.forEach(team => {
        const teamHandle = (team.team_tag || '').toLowerCase();
        if (team.leagues?.[0] && !this._leagueMap[teamHandle]) this._leagueMap[teamHandle] = team.leagues[0];
      });

      if (this._isPrefsMode) {
        this._allLeagues = leagues;
        this._renderTeamList(teams);
        this._renderLeagueCheckboxes(leagues);
      } else {
        // Row markup is already server-rendered with correct favorited state;
        // only reconcile against any selection made in another drawer instance.
        this._syncFromSelected();
      }
    }

    _wireListeners() {
      ['change', 'click', 'input'].forEach(eventType => {
        this.addEventListener(eventType, e => {
          const actionEl = e.target.closest('[data-action]');
          if (!actionEl) return;

          const { action } = actionEl.dataset;

          switch (action) {
            case 'team-checkbox':
              actionEl.checked
                ? this._selectedTeamHandles.add(actionEl.value)
                : this._selectedTeamHandles.delete(actionEl.value);
              break;

            case 'league-checkbox':
              actionEl.checked
                ? this._selectedLeagues.add(actionEl.value)
                : this._selectedLeagues.delete(actionEl.value);
              break;

            case 'remove-prefs-chip':
              this._removeFromPrefs(actionEl.dataset.handle, actionEl.closest('[data-team-chip]'));
              break;

            case 'toggle-favorite':
              this._onFavClick(actionEl);
              break;

            case 'remove-chip':
              this._removeTeam(actionEl.dataset.handle, actionEl);
              break;

            case 'save-prefs':
              this._pendingSave = this._save();
              break;

            case 'search': {
              clearTimeout(this._searchDebounce);
              const searchQuery = actionEl.value.trim().toLowerCase();
              const clearBtn = this.querySelector('[data-action="clear-search"]');
              const searchIcon = this.querySelector('[data-teams-picker-search-icon]');
              if (clearBtn) {
                clearBtn.classList.toggle('hidden', !searchQuery);
                clearBtn.classList.toggle('flex', !!searchQuery);
                searchIcon?.classList.toggle('hidden', !!searchQuery);
              }
              this._searchDebounce = setTimeout(() => this._applyFilter(searchQuery), 150);
              break;
            }

            case 'clear-search': {
              const searchInput = this.querySelector('[data-action="search"]');
              if (searchInput) searchInput.value = '';
              actionEl.classList.add('hidden');
              actionEl.classList.remove('flex');
              this.querySelector('[data-teams-picker-search-icon]')?.classList.remove('hidden');
              this._applyFilter('');
              break;
            }
          }
        });
      });

    }

    _applyFilter(searchQuery) {
      const filteredTeams = searchQuery
        ? this._allTeams.filter(team => (team.name || team.team_tag || '').replace(/-/g, ' ').toLowerCase().includes(searchQuery))
        : this._allTeams;
      if (this._isPrefsMode) this._renderTeamList(filteredTeams);
      else this._renderTeamButtons(filteredTeams);
    }

    _renderTeamList(teams) {
      const teamList = this.querySelector('[data-teams-picker-list]');
      if (!teamList) return;
      if (!teams.length) {
        teamList.innerHTML = '<li class="text-xs text-dove-grey py-4 text-center">No teams match</li>';
        return;
      }
      const template = this.querySelector('[data-team-checkbox-template]');
      // Track occurrences of each team handle to ensure unique element IDs for ADA compliance
      const seenHandles = new Map();
      teamList.replaceChildren(...teams.map(team => {
        const teamHandle = (team.team_tag || '').toLowerCase();
        const league = team.leagues?.[0] || this._leagueMap[teamHandle] || '';
        if (league) this._leagueMap[teamHandle] = league;
        const teamLabel = formatName(team.name || team.team_tag || teamHandle, team.override, { uppercase: true });

        // Differentiate duplicate team handles by appending a numeric suffix to maintain unique checkbox/label IDs
        const count = seenHandles.get(teamHandle) || 0;
        seenHandles.set(teamHandle, count + 1);
        const checkboxId = count ? `tp-cb-${teamHandle}-${count}` : `tp-cb-${teamHandle}`;

        const li = template.content.cloneNode(true).firstElementChild;
        li.dataset.hitLeague = league;

        const checkbox = li.querySelector('input');
        checkbox.id = checkboxId;
        checkbox.value = teamHandle;
        checkbox.checked = this._selectedTeamHandles.has(teamHandle);

        const label = li.querySelector('label');
        label.setAttribute('for', checkboxId);
        label.dataset.teamLabel = teamLabel;

        li.querySelector('[data-checkbox-label-slot]').textContent = teamLabel;
        fillLogoSlot(li.querySelector('[data-checkbox-logo-slot]'), team.logo, team.logo_alt);

        return li;
      }));
    }

    _removeFromPrefs(teamHandle, chipEl) {
      this._selectedTeamHandles.delete(teamHandle);
      if (chipEl) { chipEl.classList.add('opacity-30'); chipEl.style.pointerEvents = 'none'; }
      const checkbox = this.querySelector(`[data-teams-picker-list] input[value="${teamHandle}"]`);
      if (checkbox) checkbox.checked = false;
      this._save();
    }

    _renderLeagueCheckboxes(leagues) {
      const leagueList = this.querySelector('[data-leagues-picker-list]');
      if (!leagueList) return;
      const template = this.querySelector('[data-league-checkbox-template]');
      leagueList.replaceChildren(...leagues.map(league => {
        const leagueName = (league.name || '').toUpperCase();
        const el = template.content.cloneNode(true).firstElementChild;
        const checkbox = el.querySelector('input');
        checkbox.value = leagueName;
        checkbox.checked = this._selectedLeagues.has(leagueName);
        el.querySelector('[data-league-name-slot]').textContent = leagueName;
        return el;
      }));
    }

    _notifyOtherInstances() {
      const entries = [...this._selectedTeamHandles].map(handle =>
        `${handle.toUpperCase()}|${(this._leagueMap[handle] || '').toUpperCase()}`
      );
      _favTeamsCache = entries;
      const json = JSON.stringify(entries);

      this.dataset.selected = json;

      // Other instances (e.g. the always-mounted, off-screen preferences drawer) may fully
      // re-render their team/league lists in response to this. Defer so it doesn't block
      // the interaction that just happened in this instance.
      (window.requestIdleCallback ?? window.setTimeout)(() => {
        document.querySelectorAll('customer-preferences').forEach(el => {
          if (el !== this) el.dataset.selected = json;
        });

        document.querySelectorAll('template').forEach(tmpl => {
          tmpl.content.querySelectorAll('customer-preferences').forEach(el => {
            el.dataset.selected = json;
          });
        });
      });
    }

    _syncFromSelected() {
      const entries = _favTeamsCache ?? JSON.parse(this.dataset.selected || '[]');
      this._selectedTeamHandles = handlesFromEntries(entries);
      this._savedTeamHandles    = new Set(this._selectedTeamHandles);
      entries.forEach(entry => {
        const { handle, league } = parseTeamEntry(entry);
        if (league) this._leagueMap[handle] = league;
      });

      if (this._isPrefsMode) {
        const chipList = this.querySelector('[data-teams-current]');
        if (chipList) chipList.replaceChildren(...entries.map(entry => this._buildPrefsChip(entry)));
        this._applyFilter(this.querySelector('[data-action="search"]')?.value.trim().toLowerCase() || '');
        return;
      }

      this._syncListState();

      const chipList = this.querySelector('[data-favorite-teams-chips]');
      if (!chipList) return;

      chipList.querySelectorAll('[data-favorite-teams-chip]').forEach(chip => {
        if (!this._selectedTeamHandles.has(chip.dataset.favoriteTeamsChip)) chip.remove();
      });

      entries.forEach(entry => {
        const { handle } = parseTeamEntry(entry);
        if (chipList.querySelector(`[data-favorite-teams-chip="${handle}"]`)) return;
        const team = this._allTeams?.find(t => (t.team_tag || '').toLowerCase() === handle);
        if (team) this._addChip(handle, team.name || handle, team.logo || '');
      });

      this.querySelector('[data-favorite-teams-chips-section]')?.classList.toggle('hidden', !chipList.children.length);
    }

    async _save() {
      const saveButton = this.querySelector('[data-action="save-prefs"]');
      saveButton.disabled = true;
      const originalLabel = saveButton.textContent;
      saveButton.textContent = 'Saving…';

      const genderSelect = this.querySelector('[data-gender-select]');
      saveToCF(this._selectedTeamHandles, this._savedTeamHandles, this._leagueMap, {
        selectedLeagues: this._selectedLeagues,
        allLeagues:      this._allLeagues,
        ...(genderSelect && { gender: genderSelect.value || '', genderOptions: [...genderSelect.options] }),
      });

      await cfSave(window.customer_fields?.account_preferences);
      this._afterSave(originalLabel);
    }

    _afterSave(originalLabel) {
      const { savedTeams, savedLeagues, savedGender } = fetchFromCF();

      this._selectedTeamHandles = handlesFromEntries(savedTeams);
      this._savedTeamHandles    = new Set(this._selectedTeamHandles);
      savedTeams.forEach(entry => {
        const { handle, league } = parseTeamEntry(entry);
        this._leagueMap[handle] = league;
      });

      if (this._selectedLeagues) {
        this._selectedLeagues = new Set(
          savedLeagues?.map(tag => tag.replace(LEAGUE_TAG_PREFIX, '').trim().toUpperCase()) ?? []
        );
      }

      const chipList = this.querySelector('[data-teams-current]');
      if (chipList) {
        chipList.replaceChildren(...savedTeams.map(entry => this._buildPrefsChip(entry)));
      }

      this.querySelector('[data-leagues-picker-list]')?.querySelectorAll('[data-action="league-checkbox"]').forEach(checkbox => {
        checkbox.checked = this._selectedLeagues?.has(checkbox.value.toUpperCase()) ?? false;
      });

      const genderSelect = this.querySelector('[data-gender-select]');
      if (genderSelect) genderSelect.value = savedGender;

      this._applyFilter(this.querySelector('[data-action="search"]')?.value.trim().toLowerCase() || '');

      const saveButton = this.querySelector('[data-action="save-prefs"]');
      saveButton.disabled = false;
      saveButton.textContent = originalLabel;
      this._notifyOtherInstances();
      this._dirty = true;
    }

    _buildPrefsChip(entry) {
      const { handle } = parseTeamEntry(entry);
      const team = this._allTeams.find(t => (t.team_tag || '').toLowerCase() === handle);
      const teamLabel = formatName(team?.name || team?.team_tag || handle, team?.override, { uppercase: true });

      const chipEl = this.querySelector('[data-prefs-chip-template]').content.cloneNode(true).firstElementChild;
      chipEl.dataset.teamChip = handle;
      chipEl.querySelector('[data-chip-label-slot]').textContent = teamLabel;

      const removeBtn = chipEl.querySelector('[data-action="remove-prefs-chip"]');
      removeBtn.dataset.handle = handle;
      removeBtn.setAttribute('aria-label', `Remove ${teamLabel}`);

      fillLogoSlot(chipEl.querySelector('[data-chip-logo-slot]'), team?.logo, team?.logo_alt);

      return chipEl;
    }

    _onFavClick(button) {
      if (!this._isLoggedIn) { this._accountDrawerTrigger?.click(); return; }
      const teamHandle = button.dataset.team;
      const league = button.dataset.league || '';
      if (this._selectedTeamHandles.has(teamHandle)) {
        this._removeTeam(teamHandle, button);
      } else {
        const row = button.closest('[data-favorite-teams-team-row]');
        this._addTeam(teamHandle, league, row?.dataset.teamName || teamHandle, row?.dataset.teamLogo || '', button);
      }
    }

    _addTeam(teamHandle, league, teamName, teamLogo, loadingEl) {
      showRowSpinner(loadingEl);
      this._selectedTeamHandles.add(teamHandle);
      if (league && !this._leagueMap[teamHandle]) this._leagueMap[teamHandle] = league;
      this._pendingSave = whenCFReady(async () => {
        saveToCF(this._selectedTeamHandles, this._savedTeamHandles, this._leagueMap);
        const saveError = await cfSave(window.customer_fields?.account_preferences);
        hideRowSpinner(loadingEl, true);
        this._notifyOtherInstances();
        if (saveError) {
          this._savedTeamHandles = new Set(this._selectedTeamHandles);
          this._applyFromCF(teamHandle, teamName, teamLogo);
        } else {
          this._dirty = true;
        }
      });
    }

    _removeTeam(teamHandle, loadingEl) {
      const isRowButton = !!loadingEl?.querySelector('[data-favorite-teams-icon-wrap]');
      const chipWrap = isRowButton ? null : loadingEl?.querySelector('[data-chip-icon-wrap]');
      if (isRowButton) showRowSpinner(loadingEl);
      else { chipWrap?.querySelector('[data-favorite-teams-icon-close]')?.classList.add('hidden'); chipWrap?.classList.add('loading'); }
      this._selectedTeamHandles.delete(teamHandle);
      this._pendingSave = whenCFReady(async () => {
        saveToCF(this._selectedTeamHandles, this._savedTeamHandles, this._leagueMap);
        const saveError = await cfSave(window.customer_fields?.account_preferences);
        if (isRowButton) hideRowSpinner(loadingEl, false);
        else { chipWrap?.classList.remove('loading'); chipWrap?.querySelector('[data-favorite-teams-icon-close]')?.classList.remove('hidden'); }
        this._notifyOtherInstances();
        if (saveError) {
          this._savedTeamHandles = new Set(this._selectedTeamHandles);
          this._applyFromCF();
        } else {
          this._dirty = true;
        }
      });
    }

    _applyFromCF(newHandle, newName, newLogo) {
      const { savedTeams: entries } = fetchFromCF();
      this._selectedTeamHandles = handlesFromEntries(entries);
      this._savedTeamHandles    = new Set(this._selectedTeamHandles);

      this._syncListState();

      const chipList = this.querySelector('[data-favorite-teams-chips]');
      chipList?.querySelectorAll('[data-favorite-teams-chip]').forEach(chip => {
        if (!this._selectedTeamHandles.has(chip.dataset.favoriteTeamsChip)) chip.remove();
      });

      if (newHandle && this._selectedTeamHandles.has(newHandle)) {
        this._addChip(newHandle, newName, newLogo);
      }

      if (chipList) {
        this.querySelector('[data-favorite-teams-chips-section]')?.classList.toggle('hidden', !chipList.children.length);
      }
    }

    _addChip(teamHandle, teamName, teamLogo) {
      const chipList = this.querySelector('[data-favorite-teams-chips]');
      if (!chipList) return;
      if (chipList.querySelector(`[data-favorite-teams-chip="${teamHandle}"]`)) return;

      const teamLabel = formatName(teamName || teamHandle);
      const teamLeague = this._leagueMap[teamHandle] || '';

      const chipButton = this.querySelector('[data-chip-template]').content.cloneNode(true).firstElementChild;
      chipButton.dataset.favoriteTeamsChip = teamHandle;
      chipButton.dataset.action = 'remove-chip';
      chipButton.dataset.handle = teamHandle;
      chipButton.setAttribute('aria-label', `Remove ${teamLabel}`);

      chipButton.querySelector('[data-chip-label-slot]').textContent = teamLabel;

      const leagueSlot = chipButton.querySelector('[data-chip-league-slot]');
      if (leagueSlot) {
        if (teamLeague) leagueSlot.textContent = teamLeague;
        else leagueSlot.remove();
      }

      fillLogoSlot(chipButton.querySelector('[data-chip-logo-slot]'), teamLogo, '');

      chipList.appendChild(chipButton);
    }

    _syncListState() {
      this.querySelectorAll('[data-action="toggle-favorite"]').forEach(button => {
        const isSelected = this._selectedTeamHandles.has(button.dataset.team);
        button.classList.toggle('selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        hideRowSpinner(button, isSelected);
      });
    }

    _renderTeamButtons(teams) {
      const teamList = this.querySelector('[data-favorite-teams-list]');
      if (!teamList) return;
      if (!teams.length) {
        teamList.innerHTML = '<li class="py-6 text-sm text-dove-grey text-center">No teams found</li>';
        return;
      }
      const template = this.querySelector('[data-team-row-template]');
      teamList.replaceChildren(...teams.map(team => {
        const teamHandle  = (team.team_tag || '').toLowerCase();
        const league      = team.leagues?.[0] || team.league || '';
        const isFavorited = this._selectedTeamHandles.has(teamHandle);

        const li = template.content.cloneNode(true).firstElementChild;
        li.dataset.teamHandle = teamHandle;
        li.dataset.teamName   = team.name || teamHandle;
        li.dataset.teamLogo   = team.logo || '';

        const button = li.querySelector('[data-action="toggle-favorite"]');
        button.dataset.team   = teamHandle;
        button.dataset.league = league;
        button.setAttribute('aria-pressed', String(isFavorited));
        button.setAttribute('aria-label', `${isFavorited ? 'Unfavorite' : 'Favorite'} ${teamHandle.replace(/-/g, ' ')}`);
        if (isFavorited) button.classList.add('selected');

        fillLogoSlot(li.querySelector('[data-team-logo-slot]'), team.logo, team.logo_alt);
        li.querySelector('[data-team-name-slot]').innerHTML = formatProductTitle((team.name || teamHandle).replace(/-/g, ' '), [team.override]);

        li.querySelector('[data-favorite-teams-icon-empty]')?.classList.toggle('hidden', isFavorited);
        li.querySelector('[data-favorite-teams-icon-filled]')?.classList.toggle('hidden', !isFavorited);

        return li;
      }));
    }
  });
}

document.addEventListener('click', e => {
  if (e.target.closest('[data-right-drawer-type="favorite-teams-logged"]')) {
    window.dispatchEvent(new CustomEvent('modal:openById', { detail: 'favorite-teams-drawer' }));
  }
});
